import { ArrowLeft, ArrowRight } from 'lucide-react';

import { Button } from '@/shared/components/shadcn/button';
import { cn } from '@/shared/utils/cn';
import type { Table } from '@tanstack/react-table';

export interface NavigationProps<RowData> {
  table: Table<RowData>;
  className?: string;
}

export function PageNavigation<T>({ table, className }: NavigationProps<T>) {
  const getStartIndex = () =>
    table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1;

  const getEndIndex = () =>
    Math.min(
      (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
      table.getFilteredRowModel().rows.length,
    );

  return (
    <PageNavigationView
      startIndex={getStartIndex()}
      endIndex={getEndIndex()}
      className={className}
      onPrevious={table.previousPage}
      onNext={table.nextPage}
      totalCount={table.getFilteredRowModel().rows.length}
      canGoNext={table.getCanNextPage()}
      canGoPrevious={table.getCanPreviousPage()}
    />
  );
}

export function PageNavigationView({
  className,
  startIndex,
  endIndex,
  totalCount,
  onPrevious,
  onNext,
  canGoPrevious,
  canGoNext,
}: {
  className?: string;
  onPrevious: () => void;
  onNext: () => void;
  startIndex: number;
  endIndex: number;
  totalCount: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
}) {
  return (
    <div className={cn('flex items-center justify-between gap-2 px-6 py-4', className)}>
      <Button disabled={!canGoPrevious} variant="outline" onClick={onPrevious}>
        <ArrowLeft size={16} />
        <span>Previous</span>
      </Button>
      <div>
        <span className="text-base-foreground text-sm font-medium">
          {startIndex}-{endIndex}
        </span>{' '}
        <span className="text-base-muted-foreground text-sm font-normal">of {totalCount}</span>
      </div>
      <Button disabled={!canGoNext} variant="outline" onClick={onNext}>
        <span>Next</span>
        <ArrowRight size={16} />
      </Button>
    </div>
  );
}
